// DTOs del rol SUPER ADMIN (Jidoka). Cross-tenant: el super_admin bypassa RLS
// vía `app.es_super_admin = true` (policies <tabla>_super_admin).
//
// MVP: SA1 lista tenants, SA2 detalle, SA3 onboarding, SA5 salud, SA7 auditoría
// cross-tenant. SA4 (suplantación) y SA6 (operaciones de emergencia destructivas)
// quedan DIFERIDOS (tocan auth/JWT y ops peligrosas — fuera del MVP autónomo).
import { z } from 'zod';
import { eventoAuditoriaSchema, auditoriaQuerySchema } from './coordinador_auditoria.js';
export { auditoriaQuerySchema };
// =============================================================================
// SA1 — Lista de tenants
// =============================================================================
export const superTenantResumenSchema = z.object({
    id: z.string().uuid(),
    nombre: z.string(),
    nit: z.string(),
    plan: z.string(),
    activo: z.boolean(),
    plantas_count: z.number().int().min(0),
    usuarios_count: z.number().int().min(0),
});
export const superTenantsListSchema = z.object({
    tenants: z.array(superTenantResumenSchema),
});
// =============================================================================
// SA2 — Detalle de tenant
// =============================================================================
export const superTenantDetalleSchema = z.object({
    tenant: z.object({
        id: z.string().uuid(),
        nombre: z.string(),
        nit: z.string(),
        contacto_email: z.string(),
        plan: z.string(),
        activo: z.boolean(),
    }),
    plantas: z.array(z.object({
        id: z.string().uuid(),
        nombre: z.string(),
        codigo: z.string(),
        activa: z.boolean(),
    })),
    usuarios_por_rol: z.array(z.object({ rol: z.string(), total: z.number().int().min(0) })),
    totales: z.object({
        usuarios: z.number().int().min(0),
        lineas: z.number().int().min(0),
        capturas_30d: z.number().int().min(0),
    }),
});
// =============================================================================
// SA2b — Usuarios de un tenant + permiso de configuración
//
// Decisión 2026-06-11: el rol estratégico es UNO (`directivo`); la capacidad
// de configurar el tenant (A1-A6) es un PERMISO por usuario que otorga
// Jidoka (super_admin). Estos contratos soportan esa gestión.
// =============================================================================
export const superTenantUsuarioSchema = z.object({
    user_id: z.string().uuid(),
    nombre: z.string(),
    apellido: z.string(),
    email: z.string(),
    rol: z.string(),
    activo: z.boolean(),
    puede_configurar_tenant: z.boolean(),
});
export const superTenantUsuariosSchema = z.object({
    usuarios: z.array(superTenantUsuarioSchema),
});
/** Body del PATCH /tenants/:tenantId/usuarios/:userId/permiso-config */
export const superPermisoConfigSchema = z.object({
    puede_configurar_tenant: z.boolean(),
});
/** Body del POST /tenants/:tenantId/usuarios/:userId/reset-password */
export const superResetPasswordSchema = z.object({
    password: z.string().min(8, 'Mínimo 8 caracteres').max(72),
});
// =============================================================================
// SA3 — Onboarding de tenant nuevo
// =============================================================================
export const superTenantCreateSchema = z.object({
    nombre: z.string().trim().min(1).max(120),
    nit: z.string().trim().min(5).max(30),
    contacto_email: z.string().email().toLowerCase().trim(),
    plan: z.string().trim().min(1).max(40).default('mvp'),
    // Primer Admin Tenant del nuevo tenant
    admin_email: z.string().email().toLowerCase().trim(),
    admin_nombre: z.string().trim().min(1).max(80),
    admin_apellido: z.string().trim().min(1).max(80),
});
// =============================================================================
// SA5 — Salud del sistema
// =============================================================================
export const superSaludSchema = z.object({
    db_ok: z.boolean(),
    generado_at: z.string().datetime(),
    tenants_total: z.number().int().min(0),
    tenants_activos: z.number().int().min(0),
    usuarios_total: z.number().int().min(0),
    plantas_total: z.number().int().min(0),
    capturas_24h: z.number().int().min(0),
    eventos_24h: z.number().int().min(0),
    paros_abiertos: z.number().int().min(0),
    /**
     * Actividad por tenant — "¿el piloto está vivo?" de un vistazo: capturas de
     * hoy, hace cuánto fue la última y qué quedó abierto/pendiente.
     */
    actividad_tenants: z.array(z.object({
        tenant_id: z.string().uuid(),
        tenant_nombre: z.string(),
        capturas_hoy: z.number().int().min(0),
        /** Minutos desde la última captura (cualquier fecha). null = nunca. */
        ultima_captura_hace_min: z.number().int().min(0).nullable(),
        paros_abiertos: z.number().int().min(0),
        incidencias_pendientes: z.number().int().min(0),
    })),
});
// =============================================================================
// SA7 — Auditoría cross-tenant
// =============================================================================
export const superEventoAuditoriaSchema = eventoAuditoriaSchema.extend({
    tenant_id: z.string().uuid(),
    tenant_nombre: z.string().nullable(),
});
export const superAuditoriaSchema = z.object({
    recursos: z.object({
        tenants: z.array(z.object({ id: z.string().uuid(), nombre: z.string() })),
    }),
    filtros: z.object({
        desde: z.string().date().nullable(),
        hasta: z.string().date().nullable(),
        tipo_evento: z.string().nullable(),
        entidad_tipo: z.string().nullable(),
        q: z.string().nullable(),
        limit: z.number().int(),
        offset: z.number().int(),
    }),
    total: z.number().int().min(0),
    eventos: z.array(superEventoAuditoriaSchema),
});
/** Query SA7 — incluye tenant_id opcional para filtrar un tenant específico. */
export const superAuditoriaQuerySchema = auditoriaQuerySchema.extend({
    tenant_id: z.string().uuid().optional(),
});
//# sourceMappingURL=super_admin.js.map